import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    MapPin,
    Briefcase,
    ArrowUpRight,
    Heart,
    Compass,
    Coffee,
    Sparkles,
    Globe2,
} from "lucide-react";

const ROLES = [
    {
        id: "founding-pm",
        title: "Founding Product Manager",
        team: "Product",
        location: "Bengaluru · Remote (IST overlap)",
        type: "Full-time",
        salary: "Founding · equity-heavy",
        about: "Own a pillar (Sales / Ops / HR / Projects / Communication / Intelligence) end-to-end. Shape the next 12 months of the platform.",
    },
    {
        id: "ai-engineer",
        title: "AI Engineer, Co-pilot",
        team: "Engineering",
        location: "Bengaluru · Remote (Asia / EU)",
        type: "Full-time",
        salary: "₹70L – ₹1.1Cr + equity",
        about: "Build the reasoning layer over the company graph. RAG, evals, agents, observability — the whole stack.",
    },
    {
        id: "principal-eng",
        title: "Principal Engineer, Platform",
        team: "Engineering",
        location: "Remote (Global)",
        type: "Full-time",
        salary: "Senior · staff+ comp",
        about: "Own the graph store, real-time sync, multi-tenant data model and the platform's bones.",
    },
    {
        id: "design-engineer",
        title: "Design Engineer",
        team: "Design",
        location: "Bengaluru · Remote (Asia / EU)",
        type: "Full-time",
        salary: "Senior",
        about: "Ship beautiful, fast, opinionated UI on the operating layer. React + motion + taste.",
    },
    {
        id: "field-cto",
        title: "Field CTO (Enterprise)",
        team: "GTM",
        location: "Bengaluru · NYC · London",
        type: "Full-time",
        salary: "OTE comp + equity",
        about: "Partner with Fortune 1000 prospects through architecture deep-dives. Bridge eng and field.",
    },
    {
        id: "support-lead",
        title: "Customer Success Lead",
        team: "Success",
        location: "Remote (Asia / EU / US)",
        type: "Full-time",
        salary: "Mid–senior",
        about: "Stand up a global, 24/7 support practice from first principles. Process + product + people.",
    },
    {
        id: "writer",
        title: "Technical Writer / Editor",
        team: "Marketing",
        location: "Remote (Anywhere)",
        type: "Full-time",
        salary: "Senior · writing-led",
        about: "Edit the blog and docs. Set the voice. Make the platform legible to founders, operators and engineers.",
    },
    {
        id: "ops-intern",
        title: "Operations Intern (6 months)",
        team: "Operations",
        location: "Bengaluru",
        type: "Internship",
        salary: "Paid + path to FT",
        about: "Work with the founding team on hiring, finance, vendors and the operating cadence. Not a tea-and-coffee role.",
    },
];

const TEAMS = ["All", "Engineering", "Product", "Design", "GTM", "Success", "Marketing", "Operations"];

const VALUES = [
    {
        icon: Compass,
        title: "We compound, not sprint",
        body: "Most of what we build is something we'll still rely on in 5 years. We prefer fewer, better surfaces.",
        accent: "text-electric",
    },
    {
        icon: Heart,
        title: "Honesty over comfort",
        body: "Real numbers, real pushback, real changelogs. We've stopped shipping things because they didn't survive a review.",
        accent: "text-amber-glow",
    },
    {
        icon: Globe2,
        title: "Hire globally, run remote",
        body: "We're across 8 time zones already. Async-first, IST overlap for live work, paid annual offsite.",
        accent: "text-cyan-300",
    },
    {
        icon: Sparkles,
        title: "Bias toward shipping",
        body: "Most of what we launch hits production within 14 days of the first sketch. Tight loops beat tall plans.",
        accent: "text-emerald-300",
    },
];

const PERKS = [
    "Equity from day one",
    "Top-of-market base comp",
    "Wellness allowance · ₹60k / yr",
    "Annual offsite (2026: Goa)",
    "Home-office setup budget",
    "Unlimited learning + books",
    "Therapy & coaching covered",
    "32 days paid time off",
];

const HIRING_STEPS = [
    { t: "Application", d: "Tell us about you and one thing you've shipped you're proud of." },
    { t: "Founder chat", d: "30-minute call with a founder. Mutual fit, role and timing." },
    { t: "Work sample", d: "A short, paid take-home or a working session — your choice." },
    { t: "Team interviews", d: "Two craft + one teaming round. We share rubrics in advance." },
    { t: "Offer + onboarding", d: "We move fast — typical offer letter within 5 business days of step 4." },
];

export default function CareersPage() {
    const [team, setTeam] = useState("All");
    const filtered = useMemo(
        () => (team === "All" ? ROLES : ROLES.filter((r) => r.team === team)),
        [team],
    );

    return (
        <div className="pt-28 lg:pt-32" data-testid="careers-page">
            {/* Hero */}
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // careers · ApexBrain
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="careers-title"
                        >
                            Help us build the layer
                            <br />
                            <span className="gradient-text">
                                companies actually run on.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        We&apos;re a small founding team (24 people, 8 time
                        zones) shipping the operating layer for the next
                        decade of companies. Roles below are open today.
                    </p>
                </div>
            </section>

            {/* Open roles */}
            <section className="relative pb-16" data-testid="careers-roles">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
                        <div>
                            <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-glow mb-1">
                                // open roles · {ROLES.length} open
                            </p>
                            <h2 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">
                                Pick your lane.
                            </h2>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {TEAMS.map((t) => {
                                const active = team === t;
                                return (
                                    <button
                                        key={t}
                                        onClick={() => setTeam(t)}
                                        data-testid={`careers-team-${t.toLowerCase()}`}
                                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                            active
                                                ? "bg-white/[0.08] border-white/20 text-white"
                                                : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
                                        }`}
                                    >
                                        <span
                                            className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-400 animate-pulse-glow" : "bg-zinc-600"}`}
                                        />
                                        {t}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="space-y-3">
                        {filtered.map((r, i) => (
                            <motion.a
                                key={r.id}
                                href={`mailto:careers@apexbrain.in?subject=Application · ${encodeURIComponent(r.title)}`}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-30px" }}
                                transition={{ duration: 0.4, delay: i * 0.04 }}
                                whileHover={{ y: -3 }}
                                className="group relative grid grid-cols-1 md:grid-cols-12 gap-4 items-center rounded-2xl border border-white/[0.06] bg-obsidian-200/70 backdrop-blur-sm hover:border-white/15 transition-colors p-5 sm:p-6"
                                data-testid={`careers-role-${r.id}`}
                            >
                                <div className="md:col-span-6">
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] px-2 py-0.5 mb-2">
                                        <Briefcase className="w-3 h-3 text-electric" />
                                        <span className="text-[10px] font-code uppercase tracking-[0.18em] text-zinc-400">
                                            {r.team}
                                        </span>
                                    </span>
                                    <h3 className="font-outfit text-lg sm:text-xl font-semibold text-white group-hover:text-cyan-200 transition-colors">
                                        {r.title}
                                    </h3>
                                    <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed line-clamp-2">
                                        {r.about}
                                    </p>
                                </div>
                                <div className="md:col-span-3 flex md:justify-center text-xs text-zinc-400">
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3 text-zinc-500" />
                                        {r.location}
                                    </span>
                                </div>
                                <div className="md:col-span-2 text-xs font-code text-zinc-500">
                                    {r.type}
                                    <span className="block text-zinc-600 mt-0.5">
                                        {r.salary}
                                    </span>
                                </div>
                                <div className="md:col-span-1 flex md:justify-end">
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-electric group-hover:text-white transition">
                                        Apply
                                        <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:rotate-45" />
                                    </span>
                                </div>
                            </motion.a>
                        ))}
                        {filtered.length === 0 && (
                            <p className="text-sm text-zinc-500 py-10 text-center">
                                No roles open on {team} right now. Drop us a
                                line anyway —{" "}
                                <a
                                    className="text-white hover:underline"
                                    href="mailto:careers@apexbrain.in"
                                >
                                    careers@apexbrain.in
                                </a>
                                .
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="relative py-12" data-testid="careers-values">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {VALUES.map((v, i) => (
                        <motion.div
                            key={v.title}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            className="rounded-2xl border border-white/[0.06] bg-obsidian-200/70 p-6"
                        >
                            <v.icon className={`w-4 h-4 ${v.accent}`} />
                            <h3 className="mt-5 font-outfit text-base font-semibold text-white">
                                {v.title}
                            </h3>
                            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                                {v.body}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Perks + Hiring */}
            <section className="relative py-16 pb-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Perks */}
                    <div
                        className="lg:col-span-5 rounded-2xl border border-white/[0.08] bg-obsidian-200/70 p-7"
                        data-testid="careers-perks"
                    >
                        <Coffee className="w-4 h-4 text-amber-glow mb-3" />
                        <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-glow">
                            // perks
                        </p>
                        <h3 className="mt-2 font-outfit text-xl font-semibold text-white">
                            What you get, beyond the role.
                        </h3>
                        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {PERKS.map((p) => (
                                <li
                                    key={p}
                                    className="flex items-start gap-2 text-sm text-zinc-200"
                                >
                                    <span className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Hiring loop */}
                    <div
                        className="lg:col-span-7 rounded-2xl border border-electric/25 bg-gradient-to-br from-obsidian-100 to-obsidian-300 p-7 relative overflow-hidden"
                        data-testid="careers-hiring"
                    >
                        <div className="absolute -top-20 -right-20 w-64 h-64 bg-electric/15 blur-3xl rounded-full pointer-events-none" />
                        <p className="relative font-code text-[10px] uppercase tracking-[0.2em] text-electric">
                            // hiring loop · 2–3 weeks end-to-end
                        </p>
                        <h3 className="relative mt-2 font-outfit text-xl font-semibold text-white">
                            How we hire.
                        </h3>
                        <ol className="relative mt-6 space-y-3">
                            {HIRING_STEPS.map((s, i) => (
                                <li
                                    key={s.t}
                                    className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
                                >
                                    <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md bg-electric/10 border border-electric/30 font-code text-[11px] font-semibold text-electric">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div>
                                        <div className="font-outfit text-sm font-semibold text-white">
                                            {s.t}
                                        </div>
                                        <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                                            {s.d}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>
        </div>
    );
}
